import { keepPreviousData, useMutation, useQuery, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { api, ApiError, type RequestOptions } from "./api";
import { useI18n } from "./i18n";
import { useToast } from "./toast";

/**
 * `refetchInterval` makes a query live: it polls while the tab is visible (React Query pauses it in the
 * background) and refetches, if stale, when the tab or window comes back. Forms keep their own state, so
 * a refetch never resets what someone is typing. `refetchOnFocus: false` leaves out the refetch on return.
 */
export function useApi<T>(key: QueryKey, path: string | null, opts?: RequestOptions & { refetchInterval?: number; refetchOnFocus?: boolean; enabled?: boolean; keepPrevious?: boolean }) {
  return useQuery({
    queryKey: key,
    queryFn: () => api<T>(path!, opts),
    enabled: !!path && opts?.enabled !== false,
    refetchInterval: opts?.refetchInterval,
    refetchOnWindowFocus: opts?.refetchOnFocus ?? (opts?.refetchInterval ? true : undefined),
    placeholderData: opts?.keepPrevious ? keepPreviousData : undefined,
  });
}

/** Mutation that invalidates the given query keys and toasts success / error. */
export function useApiMutation<TVars, TRes = unknown>(
  fn: (vars: TVars) => Promise<TRes>,
  opts: { invalidate?: QueryKey[]; success?: string | ((r: TRes) => string | null); onSuccess?: (r: TRes, v: TVars) => void; silentError?: boolean } = {},
) {
  const qc = useQueryClient();
  const toast = useToast();
  const { te } = useI18n();
  return useMutation({
    mutationFn: fn,
    onSuccess: async (r, v) => {
      await Promise.all((opts.invalidate ?? []).map((k) => qc.invalidateQueries({ queryKey: k })));
      const msg = typeof opts.success === "function" ? opts.success(r) : opts.success;
      if (msg) toast(msg);
      opts.onSuccess?.(r, v);
    },
    onError: (e) => {
      if (opts.silentError) return;
      const err = e instanceof ApiError ? e : null;
      const detail = err?.details?.find((d) => d.message.startsWith("geo.error."));
      toast(te(detail?.message ?? err?.message, detail?.params), "error");
    },
  });
}

export const keys = {
  ports: ["ports"] as const,
  plan: (portId: string | null) => ["plan", portId] as const,
  occupancy: (portId: string | null) => ["occupancy", portId] as const,
  authority: ["authority"] as const,
  users: ["users"] as const,
  tariffs: (portId?: string | null) => ["tariffs", portId ?? "all"] as const,
  stays: ["stays"] as const,
  contracts: ["contracts"] as const,
  absences: ["absences"] as const,
  waiting: ["waiting"] as const,
  invoices: ["invoices"] as const,
  people: ["people"] as const,
  vessels: ["vessels"] as const,
  dashboard: ["dashboard"] as const,
  audit: ["audit"] as const,
  imports: ["imports"] as const,
};
